import { Controller } from "@hotwired/stimulus"

// The Planner grid: bars positioned by day through CSS custom properties.
// Dragging a bar body moves it, dragging an edge changes its duration;
// on release the new dates are PATCHed as JSON and the server's answer is
// applied to every bar with the same key (the person view repeats an item
// per assignee). A failed save reverts the bars and shows the error.
const DAY_MS = 86400000
const ZOOMS = { weeks: 28, months: 10, quarter: 4 }
const ZOOM_KEY = "planner.zoom"

export default class extends Controller {
  static targets = ["grid", "scroll", "today", "bar", "zoomButton", "error", "hint", "durationLabel"]
  static values = {
    rangeStart: String,
    days: Number,
    daysSuffix: { type: String, default: "g" },
    errorTemplate: { type: String, default: "%s" }
  }

  connect() {
    this.zoom = "weeks"
    this.applyZoom(this.savedZoom(), false)
    this.onKeydown = this.onKeydown.bind(this)
    document.addEventListener("keydown", this.onKeydown)
    if (this.hasScrollTarget) {
      this.onScroll = () => this.saveScroll()
      this.scrollTarget.addEventListener("scroll", this.onScroll, { passive: true })
      requestAnimationFrame(() => this.restoreScroll())
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKeydown)
    if (this.hasScrollTarget && this.onScroll) this.scrollTarget.removeEventListener("scroll", this.onScroll)
    clearTimeout(this.errorTimer)
  }

  get dayWidth() { return ZOOMS[this.zoom] }

  // --- zoom -----------------------------------------------------------------

  setZoom(event) { this.applyZoom(event.params.zoom, true) }

  applyZoom(zoom, save) {
    if (!ZOOMS[zoom]) zoom = "weeks"
    if (!this.hasGridTarget) return
    const scroll = this.hasScrollTarget ? this.scrollTarget : null
    const anchorDay = scroll ? scroll.scrollLeft / this.dayWidth : 0
    this.zoom = zoom
    this.gridTarget.style.setProperty("--day-w", `${this.dayWidth}px`)
    this.gridTarget.dataset.zoom = zoom
    this.zoomButtonTargets.forEach(button => button.classList.toggle("is-active", button.dataset.plannerZoomParam === zoom))
    if (scroll) scroll.scrollLeft = anchorDay * this.dayWidth
    if (save) {
      try { localStorage.setItem(ZOOM_KEY, zoom) } catch { /* private mode */ }
    }
  }

  savedZoom() {
    try { return localStorage.getItem(ZOOM_KEY) || "weeks" } catch { return "weeks" }
  }

  // --- scrolling -------------------------------------------------------------

  prev() { this.scrollByDays(-14) }
  next() { this.scrollByDays(14) }

  scrollByDays(days) {
    if (!this.hasScrollTarget) return
    this.scrollTarget.scrollBy({ left: days * this.dayWidth, behavior: "smooth" })
  }

  today() { this.scrollToToday(true) }

  scrollToToday(smooth) {
    if (!this.hasScrollTarget || !this.hasTodayTarget) return
    const left = this.todayTarget.offsetLeft - this.scrollTarget.clientWidth / 2
    this.scrollTarget.scrollTo({ left: Math.max(0, left), behavior: smooth ? "smooth" : "instant" })
  }

  get scrollKey() { return `planner.scroll:${location.pathname}${location.search}` }

  saveScroll() {
    try { sessionStorage.setItem(this.scrollKey, String(this.scrollTarget.scrollLeft / this.dayWidth)) } catch { /* ignore */ }
  }

  restoreScroll() {
    let saved = null
    try { saved = sessionStorage.getItem(this.scrollKey) } catch { /* ignore */ }
    if (saved !== null) {
      this.scrollTarget.scrollLeft = Number(saved) * this.dayWidth
    } else {
      this.scrollToToday(false)
    }
  }

  onKeydown(event) {
    const tag = event.target.tagName
    if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA" || event.target.isContentEditable) return
    if (event.key === "ArrowLeft") { event.preventDefault(); this.prev() }
    if (event.key === "ArrowRight") { event.preventDefault(); this.next() }
  }

  // --- drag ------------------------------------------------------------------

  pointerDown(event) {
    if (event.button !== 0 || this.drag) return
    const bar = event.currentTarget
    const handle = event.target.closest("[data-planner-handle]")
    this.drag = {
      bar,
      key: bar.dataset.plannerKey,
      mode: handle ? handle.dataset.plannerHandle : "move",
      startX: event.clientX,
      offset: Number(bar.dataset.plannerOffset),
      duration: Number(bar.dataset.plannerDuration),
      newOffset: Number(bar.dataset.plannerOffset),
      newDuration: Number(bar.dataset.plannerDuration),
      moved: false
    }
    bar.setPointerCapture(event.pointerId)
  }

  pointerMove(event) {
    const drag = this.drag
    if (!drag || drag.bar !== event.currentTarget) return
    const deltaDays = Math.round((event.clientX - drag.startX) / this.dayWidth)
    let offset = drag.offset
    let duration = drag.duration
    if (drag.mode === "move") {
      offset = drag.offset + deltaDays
    } else if (drag.mode === "start") {
      offset = Math.min(drag.offset + deltaDays, drag.offset + drag.duration - 1)
      duration = drag.duration - (offset - drag.offset)
    } else {
      duration = Math.max(1, drag.duration + deltaDays)
    }
    offset = Math.max(0, Math.min(offset, this.daysValue - duration))
    if (offset === drag.newOffset && duration === drag.newDuration) return

    if (!drag.moved) {
      drag.moved = true
      drag.bar.classList.add("is-dragging")
      this.gridTarget.classList.add("is-dragging")
    }
    drag.newOffset = offset
    drag.newDuration = duration
    this.barsFor(drag.key).forEach(bar => this.position(bar, offset, duration))
    this.showHint(drag)
  }

  pointerUp(event) {
    const drag = this.drag
    if (!drag || drag.bar !== event.currentTarget) return
    this.drag = null
    this.endDrag(drag)
    if (!drag.moved) return // a plain click: the drawer opens through the link
    this.suppressNextClick()
    this.persist(drag)
  }

  pointerCancel(event) {
    const drag = this.drag
    if (!drag || drag.bar !== event.currentTarget) return
    this.drag = null
    this.endDrag(drag)
    if (drag.moved) this.revert(drag)
  }

  endDrag(drag) {
    try { drag.bar.releasePointerCapture?.() } catch { /* already released */ }
    drag.bar.classList.remove("is-dragging")
    this.gridTarget.classList.remove("is-dragging")
    if (this.hasHintTarget) this.hintTarget.hidden = true
  }

  // The click that follows a drag must not open the drawer or navigate.
  suppressNextClick() {
    const stop = event => { event.preventDefault(); event.stopImmediatePropagation() }
    document.addEventListener("click", stop, { capture: true, once: true })
    setTimeout(() => document.removeEventListener("click", stop, { capture: true }), 400)
  }

  async persist(drag) {
    const startsOn = this.isoAt(drag.newOffset)
    const endsOn = this.isoAt(drag.newOffset + drag.newDuration - 1)
    try {
      const response = await fetch(drag.bar.dataset.plannerUpdateUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({ planner_item: { starts_on: startsOn, ends_on: endsOn } })
      })
      if (response.ok) {
        const data = await response.json()
        this.applyItem(data.item)
        return
      }
      const data = await response.json().catch(() => ({}))
      this.showError((data.errors || []).join(", "))
    } catch {
      this.showError("")
    }
    this.revert(drag)
  }

  applyItem(item) {
    const offset = this.offsetOf(item.starts_on)
    const duration = item.duration_days
    this.barsFor(item.key).forEach(bar => {
      bar.dataset.plannerOffset = offset
      bar.dataset.plannerDuration = duration
      this.position(bar, offset, duration)
      bar.classList.toggle("planner-bar--overdue", !!item.overdue)
      bar.classList.toggle("planner-bar--open", !!item.open_ended)
      bar.title = `${item.title} · ${this.formatDate(item.starts_on)} → ${this.formatDate(item.ends_on)} · ${duration}${this.daysSuffixValue}`
    })
    this.durationLabelTargets
      .filter(label => label.dataset.plannerKey === item.key)
      .forEach(label => { label.textContent = `${duration}${this.daysSuffixValue}` })
  }

  revert(drag) {
    this.barsFor(drag.key).forEach(bar => this.position(bar, drag.offset, drag.duration))
  }

  barsFor(key) {
    return this.barTargets.filter(bar => bar.dataset.plannerKey === key)
  }

  position(bar, offset, duration) {
    bar.style.setProperty("--offset", offset)
    bar.style.setProperty("--dur", duration)
  }

  showHint(drag) {
    if (!this.hasHintTarget) return
    const start = this.dateAt(drag.newOffset)
    const end = this.dateAt(drag.newOffset + drag.newDuration - 1)
    const hint = this.hintTarget
    hint.textContent = `${this.formatDate(start)} → ${this.formatDate(end)} · ${drag.newDuration}${this.daysSuffixValue}`
    hint.hidden = false
    const row = drag.bar.parentElement
    hint.style.left = `${drag.newOffset * this.dayWidth}px`
    hint.style.top = `${row.offsetTop - 26}px`
  }

  showError(details) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = this.errorTemplateValue.replace("%s", details)
    this.errorTarget.hidden = false
    clearTimeout(this.errorTimer)
    this.errorTimer = setTimeout(() => { this.errorTarget.hidden = true }, 5000)
  }

  // --- dates -----------------------------------------------------------------

  get rangeStart() {
    if (!this._rangeStart) {
      const [y, m, d] = this.rangeStartValue.split("-").map(Number)
      this._rangeStart = Date.UTC(y, m - 1, d)
    }
    return this._rangeStart
  }

  dateAt(offset) { return new Date(this.rangeStart + offset * DAY_MS) }

  isoAt(offset) { return this.dateAt(offset).toISOString().slice(0, 10) }

  offsetOf(iso) {
    const [y, m, d] = iso.split("-").map(Number)
    return Math.round((Date.UTC(y, m - 1, d) - this.rangeStart) / DAY_MS)
  }

  formatDate(value) {
    const date = value instanceof Date ? value : new Date(`${value}T00:00:00Z`)
    return date.toLocaleDateString(document.documentElement.lang || "it", { day: "numeric", month: "short", timeZone: "UTC" })
  }
}
